import * as React from 'react'
// libs
import { useQuery } from '@apollo/client/react'
// constants
import { STATUS_FILTERS, TIME_FRAME_FILTERS } from 'constants/home'
// graphql
import { USERS_OR_POSITIONS_QUERY } from 'graphql/user/queries'
// types
import { IPosition, IUser } from 'types/graphql'
// utils
import get from 'lodash/get'

type Props = React.PropsWithChildren<unknown>

export interface ICounters {
  assignments: number
  overdue: number
}

interface IContext {
  assignmentsAmount: number
  addToCounters: (counts: ICounters) => void
  removeFromCounters: (counts: ICounters) => void
  handleSetIsGroupedByPlan: (value: boolean) => (afterChange: () => void) => void
  handleSetStatusFilter: (value: string) => (afterChange: () => void) => void
  handleSetTimeFrameFilter: (value: string) => (afterChange: () => void) => void
  handleSetUserSearch: (value: string) => void
  handleSetUsersFilter: (member: IUser[] | IPosition[]) => void
  isGroupedByPlan: boolean
  overdueAmount: number
  statusFilter: string
  timeFrameFilter: string
  usersOrPositions: IUser[] | IPosition[] | any
  usersFilter: any
}

export const StrategyActionsBLContext = React.createContext<IContext>({} as any)

const StrategyActionsBL: React.FC<Props> = ({ children }) => {
  // state
  const [isGroupedByPlan, setIsGroupedByPlan] = React.useState<boolean>(true)
  const [statusFilter, setStatusFilter] = React.useState<string>(STATUS_FILTERS.OPEN)
  const [timeFrameFilter, setTimeFrameFilter] = React.useState<string>(TIME_FRAME_FILTERS.ALL)
  const [usersFilter, setUsersFilter] = React.useState([])
  const [userSearch, setUserSearch] = React.useState<string>('')

  const [counters, setCounters] = React.useState<ICounters>({ assignments: 0, overdue: 0 })

  const { data } = useQuery(USERS_OR_POSITIONS_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        search: userSearch
      }
    }
  })

  // methods
  const handleSetUsersFilter = (value) => setUsersFilter(value)
  const handleSetUserSearch = (value) => setUserSearch(value)

  const addToCounters = ({ assignments, overdue }: ICounters) =>
    setCounters((state) => ({ assignments: state.assignments + assignments, overdue: state.overdue + overdue }))

  const removeFromCounters = ({ assignments, overdue }: ICounters) =>
    setCounters((state) => ({ assignments: state.assignments - assignments, overdue: state.overdue - overdue }))

  const handleSetIsGroupedByPlan = (value) => (afterChange) => {
    afterChange()
    setIsGroupedByPlan(value)
  }

  const handleSetStatusFilter = (value) => (afterChange) => {
    afterChange()
    setStatusFilter(value)
  }

  const handleSetTimeFrameFilter = (value) => (afterChange) => {
    afterChange()
    setTimeFrameFilter(value)
  }

  const usersOrPositions = get(data, 'usersOrPositions.data', [])

  const value = {
    addToCounters,
    removeFromCounters,
    handleSetIsGroupedByPlan,
    handleSetStatusFilter,
    handleSetTimeFrameFilter,
    handleSetUserSearch,
    handleSetUsersFilter,
    overdueAmount: counters.overdue,
    assignmentsAmount: counters.assignments,
    isGroupedByPlan,
    statusFilter,
    timeFrameFilter,
    usersFilter,
    usersOrPositions
  }

  return <StrategyActionsBLContext.Provider value={value}>{children}</StrategyActionsBLContext.Provider>
}

export default StrategyActionsBL
